import { useMemo, lazy } from "react";
import { Outlet, RouteObject } from "react-router-dom";

import { lazyWithPreload } from "@hunkware-ui/common";
import { AppProviders, RouterErrorBounder } from "../../ui";
import { routePaths } from "../../../shared/configs";
import { getCachedManifest } from "../../../shared/libs/workflow-engine";
import { pageRegistry } from "../../../shared/libs/workflow-engine/page-registry";

const GenericStepPageLazy = lazy(() =>
  import("../../../pages/generic-step").then((m) => ({
    default: m.GenericStepPage,
  })),
);

const AppEntryLazy = lazyWithPreload(() =>
  import("../../../features/app-entry").then((module) => ({
    default: module.AppEntry,
  })),
);

const NotFoundPageLazy = lazyWithPreload(() =>
  import("../../../pages").then((module) => ({
    default: module.NotFoundPage,
  })),
);

const NotServicedPageLazy = lazyWithPreload(() =>
  import("../../../pages").then((module) => ({
    default: module.NotServicedPage,
  })),
);

/**
 * Collect unique step paths from the manifest and generate routes.
 *
 * Resolution order:
 * 1. Page registry — custom page components for steps that need unique logic
 * 2. GenericStepPage — default template that uses DynamicStepRenderer
 *    for schema-driven forms or componentKey-based components
 *
 * A catch-all route is appended so that steps created in Sanity CMS
 * (not present in the static cached manifest) still render via GenericStepPage.
 */
function buildStepRoutes(): RouteObject[] {
  const manifest = getCachedManifest();
  const seen = new Set<string>();
  const routes: RouteObject[] = [];

  // Shared steps: entry before category flows, exit after
  const sharedBefore = manifest.sharedSteps?.getStarted
    ? [manifest.sharedSteps.getStarted]
    : [];
  const sharedAfter = manifest.sharedSteps?.confirmation
    ? [manifest.sharedSteps.confirmation]
    : [];

  const allSteps = [
    ...sharedBefore,
    ...Object.values(manifest.serviceCategories).flat(),
    ...sharedAfter,
  ];

  for (const step of allSteps) {
    if (seen.has(step.path)) continue;
    seen.add(step.path);

    const PageComponent = pageRegistry[step.stepId];

    routes.push({
      path: step.path,
      element: PageComponent ? <PageComponent /> : <GenericStepPageLazy />,
    });
  }

  // Catch-all: any step path not in the static manifest (e.g. CMS-created steps)
  routes.push({
    path: "/get-started/:stepPath",
    element: <GenericStepPageLazy />,
  });

  return routes;
}

export const useAppRoutes = () => {
  const stepRoutes = useMemo(() => buildStepRoutes(), []);

  const appRoutes: RouteObject[] = useMemo(
    () => [
      {
        path: "/",
        element: (
          <AppProviders>
            <Outlet />
          </AppProviders>
        ),
        errorElement: <RouterErrorBounder />,
        children: [
          {
            path: routePaths.root,
            element: <AppEntryLazy />,
            children: [
              // Dynamic step routes from manifest
              // (includes shared get-started, category steps, and confirmation via pageRegistry)
              ...stepRoutes,

              // Not Serviced Page
              {
                path: routePaths.notServiced,
                element: <NotServicedPageLazy />,
              },

              // Not Found Page
              {
                path: "*",
                element: <NotFoundPageLazy />,
              },
            ],
          },
        ],
      },
    ],
    [stepRoutes],
  );

  return appRoutes;
};
